import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    FormGroup,
    FormControlLabel,
    Checkbox,
} from '@mui/material';

interface ExportDialogProps {
    open: boolean;
    onClose: () => void;
    availableFields: string[];
    selectedFields: string[];
    setSelectedFields: (updater: (prev: string[]) => string[]) => void;
    onExport: () => Promise<void> | void;
    isExporting?: boolean;
    visibleDefaultFields?: string[]; // optional for "Select Default"
    ignoreFields?: string[]; // optional list to hide from selection
}

export const ExportDialog: React.FC<ExportDialogProps> = ({
    open,
    onClose,
    availableFields,
    selectedFields,
    setSelectedFields,
    onExport,
    isExporting = false,
    visibleDefaultFields = [],
    ignoreFields = [],
}) => {
    const displayFields = availableFields.filter(f => !ignoreFields.includes(f));

    const handleFieldToggle = (field: string) => {
        setSelectedFields(prev =>
            prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]
        );
    };

    const handleSelectDefault = () => {
        if (visibleDefaultFields.length > 0) {
            setSelectedFields(() => [...visibleDefaultFields]);
        }
    };

    const handleSelectAll = () => {
        setSelectedFields(() => [...displayFields]);
    };

    const handleInvertSelection = () => {
        setSelectedFields(prev => displayFields.filter(f => !prev.includes(f)));
    };

    const handleDeselectAll = () => {
        setSelectedFields(() => []);
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Select Fields to Export</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    {visibleDefaultFields.length > 0 && (
                        <Button size="small" variant="outlined" color="primary" onClick={handleSelectDefault} sx={{ mr: 1 }}>
                            Select Default
                        </Button>
                    )}
                    <Button size="small" variant="outlined" color="primary" onClick={handleSelectAll} sx={{ mr: 1 }}>
                        Select All
                    </Button>
                    <Button size="small" variant="outlined" color="primary" onClick={handleInvertSelection} sx={{ mr: 1 }}>
                        Invert Selection
                    </Button>
                    <Button size="small" variant="outlined" color="primary" onClick={handleDeselectAll}>
                        Deselect All
                    </Button>
                </Box>
                <FormGroup>
                    {displayFields.map((field) => (
                        <FormControlLabel
                            key={field}
                            control={
                                <Checkbox
                                    checked={selectedFields.includes(field)}
                                    onChange={() => handleFieldToggle(field)}
                                />
                            }
                            label={field}
                        />
                    ))}
                </FormGroup>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined" color="primary">Cancel</Button>
                <Button 
                    onClick={() => void onExport()}
                    variant="contained" 
                    color="primary"
                    disabled={selectedFields.length === 0 || isExporting}
                >
                    {isExporting ? 'Exporting...' : 'Export'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExportDialog;
